import React from 'react';
import { clsx } from 'clsx';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'bordered' | 'shadow' | 'glass';
  title?: string;
  actions?: React.ReactNode;
}

export const Card: React.FC<CardProps> = ({
  variant = 'default',
  title,
  actions,
  children,
  className,
  ...props
}) => {
  const baseClasses = 'bg-white rounded-lg transition-all duration-200';
  
  const variantClasses = {
    default: 'border border-gray-200',
    bordered: 'border-2 border-gray-300',
    shadow: 'shadow-md border border-gray-100',
    glass: 'bg-white/80 backdrop-blur-sm border border-white/20'
  };

  const classes = clsx(
    baseClasses,
    variantClasses[variant],
    className
  );

  return (
    <div className={classes} {...props}>
      {(title || actions) && (
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          {title && (
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          )}
          {actions && (
            <div className="flex items-center space-x-2">
              {actions}
            </div>
          )}
        </div>
      )}
      <div className={title || actions ? 'p-6' : 'p-0'}>
        {children}
      </div>
    </div>
  );
};

export const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => (
  <div className={clsx('px-6 py-4 border-b border-gray-200', className)} {...props}>
    {children}
  </div>
);

export const CardContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => (
  <div className={clsx('p-4 sm:p-6', className)} {...props}>
    {children}
  </div>
);

export const CardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => (
  <div className={clsx('px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-200 bg-gray-50', className)} {...props}>
    {children}
  </div>
);